"use client"

import React from 'react';
import Image from 'next/image';
import styles from './ReportHero.module.css';
import { useCartStatus } from '@/hooks/useCartStatus';
import { useSnackbar } from '@/context/SnackbarContext';
import { normalizeImageUrl } from '@/utils/image';

interface ReportHeroProps {
    title: string;
    subtitle?: string;
    coverImage?: string;
    publishedDate?: string;
    pages?: number;
    format?: string;
    deliveryTime?: string;
    reportPrice?: number;
    discountedPrice?: number;
    currency?: string;
    contentId?: string | number; // Added for cart status tracking
    onPurchase?: () => void;
    onAddToCart?: () => void | Promise<void>;
    onDownloadSample?: () => void;
    coverAlt?: string;
}

const ReportHero: React.FC<ReportHeroProps> = ({
    title,
    subtitle,
    coverImage,
    publishedDate,
    pages,
    format = 'PDF',
    deliveryTime = '2-3 Business Days',
    reportPrice,
    discountedPrice,
    currency = 'USD',
    contentId,
    onPurchase,
    onAddToCart,
    onDownloadSample,
    coverAlt,
}) => {
    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
        }).format(price);
    };

    // Use cart status hook if contentId is provided
    const { isInCart, isLoading, addToCart } = contentId
        ? useCartStatus(contentId)
        : { isInCart: false, isLoading: false, addToCart: async () => { } };
    const { showSnackbar } = useSnackbar();

    const handleAddToCartClick = async () => {
        // If item is already in cart, navigate to order summary
        if (isInCart) {
            window.location.href = '/order-summary';
            return;
        }

        if (isLoading) return;

        try {
            if (onAddToCart) {
                await onAddToCart();
            } else if (contentId && reportPrice) {
                // Use the hook's addToCart with coverImage
                await addToCart(reportPrice, currency, 1, coverImage);
                showSnackbar('Report added to cart successfully', 'success');
            }
        } catch (error) {
            console.error('Add to cart error:', error);
            showSnackbar('Failed to add item to cart', 'error');
        }
    };

    return (
        <div className={styles.reportHero}>
            <div className={styles.container}>
                <div className={styles.contentWrapper}>
                    <div className={styles.heroGrid}>
                        {/* 1. Image Section */}
                        <div className={styles.imageSection}>
                            <div className={styles.imageWrapper}>
                                {coverImage ? (
                                    <Image
                                        src={normalizeImageUrl(coverImage)}
                                        alt={coverAlt || title}
                                        title={coverAlt || title}
                                        width={255}
                                        height={340}
                                        className={styles.coverImage}
                                        priority
                                    />
                                ) : (
                                    <div className={styles.placeholderImage}>
                                        <div className={styles.imageTop}>
                                            <div className={styles.imagePlaceholder}></div>
                                        </div>
                                        <div className={styles.imageBottom}>
                                            <div className={styles.imageText}>
                                                {(title || '').split(' ')[0]}<br />
                                                {(title || '').split(' ')[1] || ''} (MAbs)
                                            </div>
                                            <div className={styles.imageSubtext}>
                                                Global Market Report
                                            </div>
                                        </div>
                                        <div className={styles.imageLogo}></div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 2. Metadata Section */}
                        <div className={styles.metaSection}>
                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Published :</span>
                                <span className={styles.metaValue}>{publishedDate || 'December 2025'}</span>
                            </div>

                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Pages :</span>
                                <span className={styles.metaValue}>{pages || '250'}</span>
                            </div>

                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Format :</span>
                                <span className={styles.metaValue}>{format}</span>
                            </div>

                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Delivery Time :</span>
                                <span className={styles.metaValue}>{deliveryTime}</span>
                                <div className={styles.tooltipContainer}>
                                    <span className={styles.infoIcon}>ⓘ</span>
                                    <div className={styles.tooltipText}>
                                        Why 2-3 days? We update the report with the latest data and news before delivery. Let us know if you need us to expedite.
                                    </div>
                                </div>
                            </div>

                            <div className={styles.priceRow}>
                                <span className={styles.priceLabel}>Report Price :</span>
                                {discountedPrice && discountedPrice > 0 ? (
                                    <>
                                        {reportPrice && (
                                            <span className={styles.originalPrice}>
                                                {formatPrice(reportPrice)}
                                            </span>
                                        )}
                                        <span className={styles.discountedPrice}>
                                            {formatPrice(discountedPrice)}
                                        </span>
                                    </>
                                ) : (
                                    reportPrice && (
                                        <span className={styles.metaValue}>
                                            {formatPrice(reportPrice)}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>

                        {/* 3. Description Section */}
                        <div className={styles.rightSection}>
                            <h1 className={styles.title}>{title}</h1>

                            {subtitle && (
                                <p className={styles.subtitle}>{subtitle}</p>
                            )}

                            <div className={styles.actionRow}>
                                <button className={styles.btnPurchase} onClick={onPurchase}>
                                    Purchase This Report
                                </button>

                                <button className={styles.btnDownload} onClick={onDownloadSample}>
                                    Download Sample PDF
                                </button>

                                <button
                                    className={styles.btnAddToCart}
                                    onClick={handleAddToCartClick}
                                    disabled={isLoading}
                                    style={{
                                        backgroundColor: isInCart ? '#217B6C' : undefined,
                                        color: isInCart ? '#ffffff' : undefined,
                                        borderColor: isInCart ? 'transparent' : undefined
                                    }}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            Adding...
                                        </>
                                    ) : isInCart ? (
                                        '✓ View in Cart'
                                    ) : (
                                        'Add to Cart'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportHero;